import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, useLocation } from 'react-router';
import styled from 'styled-components';
import agentAccessApi, { AgentAccess, AgentConsentRequestDto } from '@api/agentAccessApi';
import Button from '@components/common/buttons/Button';
import { getSemesterAuth } from '@state/semester/selectors';
import colors from '@utils/colors';
import { LOGIN } from '@utils/constants/routes';
import { rememberAgentConnect } from '@utils/pendingAgentConnect';
import { LargeText, MediumText, RegularText } from '@utils/typography';
import t from '@i18n/t';
import { ReactComponent as ShovelLogo } from '../../assets/images/shovel-logo.svg';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; request: AgentConsentRequestDto }
  | { kind: 'expired' }
  | { kind: 'notAllowed' }
  | { kind: 'error' }
  // Sent back to an app that opens with its own link (cursor://…): this tab stays, so say it's done.
  | { kind: 'done' };

/**
 * An AI app (ChatGPT, Claude, ...) sends the student here to approve its connection to their Shovel. They pick View
 * only or View and edit, and go back to the app.
 */
const ConnectAiAppPage: FC = () => {
  const location = useLocation();
  const auth = useSelector(getSemesterAuth);
  const requestId = new URLSearchParams(location.search).get('request') ?? '';
  const isLoggedIn = !!auth && !!auth.email;

  const [state, setState] = useState<LoadState>(requestId ? { kind: 'loading' } : { kind: 'expired' });
  // View and edit is the default: Shovel is most useful when the assistant can plan for the student, and their AI app
  // still asks before each change.
  const [access, setAccess] = useState<AgentAccess>('edit');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!isLoggedIn || !requestId) return;
    agentAccessApi.getConsentRequest(requestId).then(response => {
      if (response.ok && response.data) {
        setState({ kind: 'ready', request: response.data });
      } else if (response.status === 404) {
        setState({ kind: 'expired' });
      } else if (response.status === 401 || response.status === 403) {
        setState({ kind: 'notAllowed' });
      } else {
        setState({ kind: 'error' });
      }
    });
  }, [isLoggedIn, requestId]);

  if (!isLoggedIn) {
    if (requestId) rememberAgentConnect(requestId);
    return <Redirect to={LOGIN} />;
  }

  const finish = async (approve: boolean) => {
    setSending(true);
    const response = approve ? await agentAccessApi.approve(requestId, access) : await agentAccessApi.deny(requestId);
    if (response.ok && response.data?.redirectUrl) {
      window.location.href = response.data.redirectUrl;
      setState({ kind: 'done' });
      return;
    }
    setSending(false);
    setState(response.status === 404 ? { kind: 'expired' } : { kind: 'error' });
  };

  return (
    <Page>
      <Card>
        <Logo />
        {state.kind === 'loading' && <MediumText>{t.CONNECT_AI_LOADING}</MediumText>}
        {state.kind === 'expired' && <Message>{t.CONNECT_AI_EXPIRED}</Message>}
        {state.kind === 'done' && <Message>{t.CONNECT_AI_DONE}</Message>}
        {state.kind === 'notAllowed' && <Message>{t.CONNECT_AI_NOT_ALLOWED}</Message>}
        {state.kind === 'error' && <Message>{t.CONNECT_AI_ERROR}</Message>}
        {state.kind === 'ready' && (
          <>
            <Title strong>{t.CONNECT_AI_TITLE(state.request.clientName)}</Title>
            <Host>{t.CONNECT_AI_FROM(state.request.clientHost)}</Host>
            <Choices role="group" aria-label={t.CONNECT_AI_ACCESS_LABEL}>
              <Choice aria-pressed={access === 'edit'} selected={access === 'edit'} onClick={() => setAccess('edit')}>
                <ChoiceTitle strong>{t.CONNECT_AI_EDIT_TITLE}</ChoiceTitle>
                <MediumText>{t.CONNECT_AI_EDIT_TEXT}</MediumText>
              </Choice>
              <Choice aria-pressed={access === 'view'} selected={access === 'view'} onClick={() => setAccess('view')}>
                <ChoiceTitle strong>{t.CONNECT_AI_VIEW_TITLE}</ChoiceTitle>
                <MediumText>{t.CONNECT_AI_VIEW_TEXT}</MediumText>
              </Choice>
            </Choices>
            <Fine>{t.CONNECT_AI_FINE_PRINT}</Fine>
            <Buttons>
              <Button withoutBorder disabled={sending} onClick={() => finish(false)}>
                {t.CANCEL}
              </Button>
              <Button filled disabled={sending} onClick={() => finish(true)}>
                {t.CONNECT_AI_ALLOW}
              </Button>
            </Buttons>
          </>
        )}
      </Card>
    </Page>
  );
};

export default ConnectAiAppPage;

// Fixed and scrolling on its own: the app shell doesn't scroll, and short windows must still reach the buttons.
const Page = styled.div`
  position: fixed;
  inset: 0;
  overflow-y: auto;
  box-sizing: border-box;
  display: flex;
  padding: 24px 16px;
  background-color: ${props => props.theme.backgroundDark || props.theme.background};
`;

const Card = styled.div`
  margin: auto;
  width: 100%;
  max-width: 440px;
  box-sizing: border-box;
  padding: 32px 28px 24px;
  border-radius: 16px;
  background-color: ${props => props.theme.background};
  box-shadow: 0 8px 30px ${props => props.theme.boxShadow};
  display: flex;
  flex-direction: column;
`;

const Logo = styled(ShovelLogo)`
  width: 44px;
  height: 44px;
  margin-bottom: 20px;
`;

const Title = styled(LargeText)`
  display: block;
  color: ${props => props.theme.textStrongColor};
  line-height: 1.3;
`;

const Host = styled(MediumText)`
  display: block;
  margin-top: 6px;
  color: ${colors.gray};
`;

const Message = styled(RegularText)`
  display: block;
  line-height: 1.5;
`;

const Choices = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 22px;
`;

const Choice = styled.button<{ selected: boolean }>`
  text-align: left;
  font: inherit;
  cursor: pointer;
  padding: 14px 16px;
  border-radius: 12px;
  border: 2px solid ${props => (props.selected ? colors.primaryPurple : props.theme.borderColor)};
  background-color: ${props =>
    props.selected ? `color-mix(in srgb, ${colors.primaryPurple} 7%, ${props.theme.background})` : 'transparent'};
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: ${props => props.theme.textColor};

  &:hover {
    background-color: ${props =>
      `color-mix(in srgb, ${colors.primaryPurple} ${props.selected ? 9 : 4}%, ${props.theme.background})`};
  }
`;

// A heading for each choice: bigger and bolder than the text under it.
const ChoiceTitle = styled(RegularText)`
  font-size: 17px;
  font-weight: 700;
  color: ${props => props.theme.textStrongColor};
`;

const Fine = styled(MediumText)`
  display: block;
  margin-top: 16px;
  color: ${colors.gray};
  line-height: 1.45;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 22px;
`;
